import { Box, Checkbox } from '@mui/material';
import type { GridColDef, GridRowId } from '@mui/x-data-grid-pro';

import { ConditionalTooltip } from './ConditionalTooltip';
import { getProjectArtifactRowId, type ProjectArtifactsGridSelection } from './selection';
import {
  ARTIFACT_SELECTION_FIELD,
  SELECT_CONTROL_CLASS,
  SELECTION_COLUMN_WIDTH,
} from './selectionStyles';
import type { ProjectArtifact } from '../../types';

interface SelectControlProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  /** Listened for on the wrapper: a disabled checkbox fires no pointer events, and
   *  that is exactly when the explanation is needed. */
  tooltip?: string;
}

/** Positioned out of its collapsed cell into the Name gutter by `selectionColumnSx`. */
const SelectControl = ({ checked, disabled, onChange, label, tooltip }: SelectControlProps) => (
  <ConditionalTooltip title={tooltip}>
    <Box className={SELECT_CONTROL_CLASS}>
      <Checkbox
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={(_, next) => onChange(next)}
        inputProps={{ 'aria-label': label }}
        sx={{ position: 'absolute', inset: 0, padding: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}
      />
    </Box>
  </ConditionalTooltip>
);

/**
 * Row checkboxes and select-all, pinned 1px wide with the control positioned out
 * into the Name gutter. Unsortable/unhideable — moving it would strand selection.
 *
 * `getDisplayedRowIds` reads the grid's current row order at click time, which is
 * what select-all takes its capful from.
 *
 * Takes a `getSelection` getter, not `selection` itself: the column definitions
 * this builds into feed the grid's `columns` array, and that array's identity
 * must survive a `selection` object that gets rebuilt on every artifact-list
 * poll (OR-6785) even when nothing about the selection actually changed. Reading
 * through a getter at render time keeps the checkbox state live without forcing
 * `columns` to recompute (which resets user column-resize on every rebuild).
 */
export const createArtifactSelectionColumn = (
  getSelection: () => ProjectArtifactsGridSelection,
  getDisplayedRowIds: () => readonly GridRowId[]
): GridColDef<ProjectArtifact> => ({
  field: ARTIFACT_SELECTION_FIELD,
  headerName: '',
  width: SELECTION_COLUMN_WIDTH,
  minWidth: SELECTION_COLUMN_WIDTH,
  maxWidth: SELECTION_COLUMN_WIDTH,
  resizable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  disableReorder: true,
  renderHeader: () => {
    const selection = getSelection();
    return (
      <SelectControl
        checked={selection.selectAll.allSelected}
        // Never indeterminate: the dash reads as "clear these", which is the pill's
        // job. An empty box means what it looks like — take them all.
        disabled={!selection.selectAll.allSelected && !selection.selectAll.apply}
        onChange={() =>
          selection.selectAll.allSelected
            ? selection.clearAll()
            : selection.selectAll.apply?.(getDisplayedRowIds())
        }
        label="Select all rows"
      />
    );
  },
  renderCell: (params) => {
    const selection = getSelection();
    const rowId = getProjectArtifactRowId(params.row);
    const isChecked = selection.selectedRowIds.has(rowId);
    const selectability = selection.getRowSelectability(params.row);
    const refusedReason = selectability.selectable ? undefined : selectability.refusedReason;
    // A refusal with no reason renders nothing — the row's gutter stays empty.
    if (!selectability.selectable && !refusedReason) return null;
    return (
      <SelectControl
        checked={isChecked}
        disabled={Boolean(refusedReason)}
        onChange={(checked) => selection.toggleRow(rowId, checked)}
        label={isChecked ? 'Unselect row' : 'Select row'}
        tooltip={refusedReason}
      />
    );
  },
});
